import { useUser } from '@clerk/react';
import { Button } from '@vrplatform/components';
import {
  resolvePortalApp,
  useApiEndpoint,
  useDashboard,
  useMe,
  useOwnerPortalContact,
  useTeam,
  useTrackingSignOut,
} from '@vrplatform/data';
import { theme as defaultTheme } from '@vrplatform/theme';
import {
  LoadingOverlay,
  MantineThemeProvider as MantineProvider,
  Stack,
  createTheme,
} from '@mantine/core';
import {
  DEMO_TEST_PARTNER_TENANT_ID,
  VRP_TENANT_ID,
  getOwnerPortalColor,
} from '@vrplatform/ui-common';
import { type ReactNode, Suspense, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { AppLayout } from '../layout/AppLayout';
import { NavbarRoute } from '../layout/navbar';
import { FinalyticMigrationModal } from '../modals/FinalyticMigrationModal';
import { OwnerInviteLoggedInModal } from '../modals/OwnerInviteLoggedInModal';
import { WelcomeUserModal } from '../modals/WelcomeUserModal';
import { OwnerNavigation, PartnerNavigation } from '../navigation';
import { NavbarSyncsIconButton } from '../navigation/_components';
import { VrpNavigation } from '../navigation/VrpNavigation';
import { NavigationSection } from '../navigation/NavigationSection';
import { QuickSearchRoute } from '../navigation/QuickSearchRoute';
import { SetupLoadingScreen } from '../views/onboarding/components/SetupLoadingScreen';
import EmptyTeamView from '../components/EmptyTeamView';
import { DashboardRoutes } from './dashboard';
import {
  EmptyViewWrapper,
  FullPageLoadingIndicator,
  TeamSwitch,
} from '../components';
import { resolveUserLoadedViewState } from './AppRoutes.utils';
import { lazyWithRetry } from './dashboard/_utils';
import { PmNavigation as VrTrustPmNavigationContent } from '@vrtrust/navigation/PmNavigation';
import { SettingsNavigation as VrTrustSettingsNavigationContent } from '@vrtrust/navigation/SettingsNavigation';

const ConnectionDetailDrawer = lazyWithRetry(() =>
  import('@vrtrust/drawers/connection-drawer').then((module) => ({
    default: module.ConnectionDetailDrawer,
  }))
);
const ReservationDetailDrawer = lazyWithRetry(() =>
  import('@vrtrust/drawers/reservation-drawer').then((module) => ({
    default: module.ReservationDetailDrawer,
  }))
);
const BankRecordDetailDrawer = lazyWithRetry(() =>
  import('@vrtrust/drawers/bank-record-drawer').then((module) => ({
    default: module.BankRecordDetailDrawer,
  }))
);
const BankTransferDetailDrawer = lazyWithRetry(() =>
  import('@vrtrust/drawers/bank-transfer-drawer').then((module) => ({
    default: module.BankTransferDetailDrawer,
  }))
);
const DepositDetailDrawer = lazyWithRetry(() =>
  import('@vrtrust/drawers/deposit-drawer').then((module) => ({
    default: module.DepositDetailDrawer,
  }))
);
const ExpenseDetailDrawer = lazyWithRetry(() =>
  import('@vrtrust/drawers/expense-drawer').then((module) => ({
    default: module.ExpenseDetailDrawer,
  }))
);
const ListingDetailDrawer = lazyWithRetry(() =>
  import('@vrtrust/drawers/listing-drawer').then((module) => ({
    default: module.ListingDetailDrawer,
  }))
);
const OwnerDetailDrawer = lazyWithRetry(() =>
  import('@vrtrust/drawers/owner-drawers').then((module) => ({
    default: module.OwnerDetailDrawer,
  }))
);
const VendorDetailDrawer = lazyWithRetry(() =>
  import('@vrtrust/drawers/vendor-drawer').then((module) => ({
    default: module.VendorDetailDrawer,
  }))
);
const PayoutDetailDrawer = lazyWithRetry(() =>
  import('@vrtrust/drawers/payout-drawer').then((module) => ({
    default: module.PayoutDetailDrawer,
  }))
);
const AuditHistoryDrawer = lazyWithRetry(() =>
  import('@vrtrust/drawers/audit-event-drawer').then((module) => ({
    default: module.AuditHistoryDrawer,
  }))
);
const SyncDetailDrawer = lazyWithRetry(() =>
  import('@vrtrust/drawers/sync-drawer').then((module) => ({
    default: module.SyncDetailDrawer,
  }))
);
const AddTeamDrawer = lazyWithRetry(() =>
  import('../drawers').then((module) => ({ default: module.AddTeamDrawer }))
);
const OnboardingRoutes = lazyWithRetry(() =>
  import('../views/onboarding').then((module) => ({
    default: module.OnboardingRoutes,
  }))
);

const VrTrustPmNavigation = () => (
  <VrTrustPmNavigationContent
    NavbarRoute={NavbarRoute}
    NavbarSyncsIconButton={NavbarSyncsIconButton}
    NavigationSection={NavigationSection}
    QuickSearchRoute={QuickSearchRoute}
  />
);

const VrTrustSettingsNavigation = () => (
  <VrTrustSettingsNavigationContent
    NavbarRoute={NavbarRoute}
    NavigationSection={NavigationSection}
  />
);

export const AppRoutes = () => {
  const location = useLocation();
  const user = useMe();
  const [team, , isFetchingTeam] = useTeam();

  return (
    <>
      {/** biome-ignore lint/correctness/useUniqueElementIds: static id is fine */}
      <LoadingOverlay
        visible={
          isFetchingTeam &&
          !!team?.id &&
          !!user?.id &&
          !['/setup', '/verify-email', '/sign-up'].includes(location.pathname)
        }
        opacity={0.5}
        id="loading-overlay"
        overlayProps={{ radius: 'sm', blur: 2, color: '#000' }}
        loaderProps={{
          color: '#000',
        }}
      />
      <RouteSwitch />
    </>
  );
};

const RouteSwitch = () => {
  const user = useMe();
  const [, , , , sessionQueryError] = useTeam();

  if (!user?.id) {
    // `/me` failures leave the user empty, so surface the recovery screen
    // instead of keeping the startup spinner alive forever.
    if (sessionQueryError) return <ErrorView error={sessionQueryError} />;

    return <LoadingSubscription />;
  }

  return <UserLoaded />;
};

type TeamLoadErrorActionsProps = {
  canResetApiHost: boolean;
  onRefresh: () => void;
  onResetApiHost: () => void;
  onSignOut: () => void;
  teamSwitch?: ReactNode;
};

export const TeamLoadErrorActions = ({
  canResetApiHost,
  onRefresh,
  onResetApiHost,
  onSignOut,
  teamSwitch,
}: TeamLoadErrorActionsProps) => {
  return (
    <Stack gap="sm">
      {canResetApiHost && (
        <Button onClick={onResetApiHost} variant="primary">
          Use default API
        </Button>
      )}
      <Button
        variant={canResetApiHost ? 'default' : 'primary'}
        onClick={onRefresh}
      >
        Refresh
      </Button>
      {teamSwitch || (
        <TeamSwitch
          variant="custom"
          header={null}
          visibility="when-switchable"
          isNavigationExpanded
          renderTrigger={() => (
            <Button sx={{ width: '100%' }} variant="default">
              Switch to another team
            </Button>
          )}
          tone="default"
        />
      )}
      <Button onClick={onSignOut}>Sign out</Button>
    </Stack>
  );
};

// PRO-18809: VRIntegrations teams are served by the ui-vrintegrations app.
// The portal router normally loads that app; any other host (local dev,
// reserved previews) states it instead of rendering a missing product.
const VriTeamView = () => {
  const signOut = useTrackingSignOut();

  return (
    <EmptyViewWrapper
      title="This team opens in VRIntegrations"
      description="VRIntegrations teams load in their own app. Reload the page to open it, or switch to another team."
      actions={
        <TeamLoadErrorActions
          canResetApiHost={false}
          onRefresh={() => window.location.reload()}
          onResetApiHost={() => undefined}
          onSignOut={() => {
            void signOut({ trackEvent: false });
          }}
        />
      }
    />
  );
};

const ErrorView = ({ error }: { error: Error | null | undefined }) => {
  const [, refreshTeam] = useTeam();
  const signOut = useTrackingSignOut();
  const { apiHost, setApiHost, storedApiHost } = useApiEndpoint();
  const isLocalApiHost = apiHost.includes('localhost');
  const canResetApiHost = isLocalApiHost || !!storedApiHost;
  const description = isLocalApiHost
    ? `Could not reach the local API at ${apiHost}. Start it locally or switch back to the default API.`
    : error?.message ||
      'Please try again and contact support if the issue persists';

  return (
    <EmptyViewWrapper
      title="Failed to load team"
      description={description}
      actions={
        <TeamLoadErrorActions
          canResetApiHost={canResetApiHost}
          onRefresh={() => {
            void refreshTeam();
          }}
          onResetApiHost={() => setApiHost(undefined)}
          onSignOut={() => {
            void signOut({ trackEvent: false });
          }}
        />
      }
    />
  );
};

const UserLoaded = () => {
  const { user: clerkUser } = useUser();
  const user = useMe();
  const [team, , isFetchingTeam, , teamQueryError] = useTeam();
  const location = useLocation();
  const viewState = resolveUserLoadedViewState({
    teamId: team?.id,
    isFetchingTeam,
    teamQueryError,
    isOnboarding: !!team?.isOnboarding,
    pathname: location.pathname,
    canAccessTeam: user.canAccessTeam,
    isVriTeam: !!team?.id && resolvePortalApp(team) === 'vri',
  });

  switch (viewState) {
    case 'setup-loading':
      return (
        <SetupLoadingScreen
          phase="workspace"
          requiresPayment={clerkUser?.unsafeMetadata?.onboardingV2 === true}
          teamType={clerkUser?.unsafeMetadata?.tenantType}
        />
      );
    case 'loading':
      return <FullPageLoadingIndicator message="Loading Dashboard..." />;
    case 'error':
      return <ErrorView error={teamQueryError} />;
    case 'empty-team':
      return <EmptyTeamView />;
    case 'vri-team':
      return <VriTeamView />;
    case 'onboarding':
      return <Onboarding />;
    case 'dashboard':
      return <Dashboard />;
  }

  throw new Error(`Unsupported user loaded view state: ${viewState}`);
};

const Onboarding = () => {
  return (
    <>
      <Suspense
        fallback={<FullPageLoadingIndicator message="Loading Dashboard..." />}
      >
        <OnboardingRoutes />
      </Suspense>
      <WelcomeUserModal />
      <FinalyticMigrationModal />
    </>
  );
};

const Dashboard = () => {
  const [dashboard] = useDashboard();

  if (dashboard === 'owner') {
    return <OwnerPortal />;
  }

  return <App />;
};

function OwnerPortal() {
  const [team] = useTeam();
  const primaryColor = getOwnerPortalColor(team.colorPrimary);

  const theme = useMemo(() => {
    if (!primaryColor) return undefined;

    return createTheme({
      ...defaultTheme,
      primaryColor,
    });
  }, [primaryColor]);

  if (!theme) return <App />;

  return (
    <MantineProvider theme={theme}>
      <App />
    </MantineProvider>
  );
}

function App() {
  const [dashboard] = useDashboard();
  const location = useLocation();
  const [{ id: teamId }] = useTeam();
  const { contactId } = useOwnerPortalContact();

  const DashboardNavigation = useMemo(() => {
    if (dashboard === 'owner') return OwnerNavigation;
    if (dashboard === 'partner')
      return teamId === VRP_TENANT_ID ? VrpNavigation : PartnerNavigation;
    if (dashboard === 'overview') return PartnerNavigation;
    return VrTrustPmNavigation;
  }, [dashboard, teamId]);

  const isSettingsView = location.pathname.startsWith('/settings');

  if (!contactId && dashboard === 'owner') {
    return <DashboardRoutes />;
  }

  return (
    <>
      <AppLayout
        navigation={
          isSettingsView ? VrTrustSettingsNavigation : DashboardNavigation
        }
        drawers={[
          SyncDetailDrawer,
          ConnectionDetailDrawer,
          ReservationDetailDrawer,
          OwnerDetailDrawer,
          VendorDetailDrawer,
          ListingDetailDrawer,
          AddTeamDrawer,
          ExpenseDetailDrawer,
          DepositDetailDrawer,
          BankRecordDetailDrawer,
          PayoutDetailDrawer,
          BankTransferDetailDrawer,
          ...(dashboard === 'propertyManager' ? [AuditHistoryDrawer] : []),
        ]}
      >
        <DashboardRoutes />
      </AppLayout>
      <WelcomeUserModal />

      {/*<OwnerTaxInfoModal />*/}
      <OwnerInviteLoggedInModal />
      <FinalyticMigrationModal />
    </>
  );
}

const LoadingSubscription = () => {
  const location = useLocation();
  const [{ id: teamId, billingPartnerId, type }] = useTeam();
  const [, refreshTeam] = useTeam();
  const { user } = useUser();
  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(async () => {
      await user?.reload(); // refetch on interval clerk public metadata with tenant_ids
      // VRT-710: a reload that finishes after `/me` settled must not start a
      // second `/me` behind the loaded dashboard.
      if (isMounted.current && (teamId || user?.publicMetadata?.user_id))
        refreshTeam();
    }, 3000);

    return () => clearInterval(interval);
  }, [teamId, refreshTeam, user]);

  if (['/setup', '/verify-email', '/sign-up'].includes(location.pathname))
    return (
      <SetupLoadingScreen
        phase="workspace"
        requiresPayment={
          teamId
            ? [VRP_TENANT_ID, DEMO_TEST_PARTNER_TENANT_ID].includes(
                billingPartnerId ?? ''
              )
            : user?.unsafeMetadata?.onboardingV2 === true
        }
        teamType={teamId ? type : user?.unsafeMetadata?.tenantType}
      />
    );

  return <FullPageLoadingIndicator message="Loading Dashboard..." />;
};
